import { useState, useCallback } from 'react'
import type {
  AppSettings,
  NotificationEventType,
  NotificationChannel,
  NotificationTypePreference,
  QuietHours
} from '@/core/types'
import { Bell, Clock, Globe, Inbox, Moon, Volume2 } from 'lucide-react'

// ============================================================================
// Event type display config
// ============================================================================

interface EventTypeConfig {
  label: string
  description: string
  icon: React.ReactNode
}

const EVENT_TYPE_CONFIG: Record<NotificationEventType, EventTypeConfig> = {
  follow_up_due: {
    label: 'Follow-up Due',
    description: 'When a follow-up reminder is due for an application',
    icon: <Clock className="w-4 h-4" />
  },
  application_status_change: {
    label: 'Application Status Change',
    description: 'When an application moves to a new stage',
    icon: <Globe className="w-4 h-4" />
  },
  weekly_digest: {
    label: 'Weekly Digest',
    description: 'Summary of your weekly application activity',
    icon: <Inbox className="w-4 h-4" />
  }
}

const SNOOZE_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 1440, label: '24 hours' }
]

// ============================================================================
// Props
// ============================================================================

interface NotificationSettingsPanelProps {
  settings: AppSettings
  onUpdate: (settings: AppSettings) => void
}

// ============================================================================
// Main Component
// ============================================================================

export function NotificationSettingsPanel({ settings, onUpdate }: NotificationSettingsPanelProps) {
  const prefs = settings.notifications
  const [localPrefs, setLocalPrefs] = useState(prefs)

  const updatePrefs = useCallback(
    (updated: typeof prefs) => {
      setLocalPrefs(updated)
      onUpdate({ ...settings, notifications: updated })
    },
    [settings, onUpdate]
  )

  const toggleMaster = useCallback(() => {
    updatePrefs({ ...localPrefs, enabled: !localPrefs.enabled })
  }, [localPrefs, updatePrefs])

  const updateQuietHours = useCallback(
    (patch: Partial<QuietHours>) => {
      updatePrefs({
        ...localPrefs,
        quietHours: { ...localPrefs.quietHours, ...patch }
      })
    },
    [localPrefs, updatePrefs]
  )

  const updatePerType = useCallback(
    (eventType: NotificationEventType, patch: Partial<NotificationTypePreference>) => {
      updatePrefs({
        ...localPrefs,
        perType: {
          ...localPrefs.perType,
          [eventType]: { ...localPrefs.perType[eventType], ...patch }
        }
      })
    },
    [localPrefs, updatePrefs]
  )

  const toggleChannel = useCallback(
    (eventType: NotificationEventType, channel: NotificationChannel) => {
      const current = localPrefs.perType[eventType]
      const has = current.channels.includes(channel)
      const channels = has
        ? current.channels.filter(c => c !== channel)
        : [...current.channels, channel]
      updatePerType(eventType, { channels })
    },
    [localPrefs, updatePerType]
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 dark:bg-blue-500/10 rounded-lg">
          <Bell className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Notification Preferences</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Configure which events trigger notifications and how they are delivered.</p>
        </div>
      </div>

      {/* Master Toggle */}
      <section className="p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Volume2 className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">Enable Notifications</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Master switch for all notifications</p>
            </div>
          </div>
          <ToggleSwitch checked={localPrefs.enabled} onChange={toggleMaster} />
        </div>
      </section>

      {/* Per-Event-Type Preferences */}
      <section className={`space-y-3 ${!localPrefs.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Event Types</h4>

        {(Object.entries(EVENT_TYPE_CONFIG) as [NotificationEventType, EventTypeConfig][]).map(
          ([eventType, config]) => (
            <EventTypeCard
              key={eventType}
              eventType={eventType}
              config={config}
              preference={localPrefs.perType[eventType]}
              onToggle={() => updatePerType(eventType, { enabled: !localPrefs.perType[eventType].enabled })}
              onToggleChannel={(ch) => toggleChannel(eventType, ch)}
              onSnoozeChange={(val) => updatePerType(eventType, { snoozeDurationMinutes: val })}
            />
          )
        )}
      </section>

      {/* Quiet Hours */}
      <section className={`p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 ${!localPrefs.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Moon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">Quiet Hours</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Suppress notifications during set hours</p>
              </div>
            </div>
            <ToggleSwitch
              checked={localPrefs.quietHours.enabled}
              onChange={() => updateQuietHours({ enabled: !localPrefs.quietHours.enabled })}
            />
          </div>

          {localPrefs.quietHours.enabled && (
            <div className="flex items-center gap-4 pl-8">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 dark:text-gray-300">From</label>
                <TimeInput
                  hour={localPrefs.quietHours.startHour}
                  minute={localPrefs.quietHours.startMinute}
                  onChangeHour={(h) => updateQuietHours({ startHour: h })}
                  onChangeMinute={(m) => updateQuietHours({ startMinute: m })}
                />
              </div>
              <span className="text-gray-400 dark:text-gray-500">&mdash;</span>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 dark:text-gray-300">To</label>
                <TimeInput
                  hour={localPrefs.quietHours.endHour}
                  minute={localPrefs.quietHours.endMinute}
                  onChangeHour={(h) => updateQuietHours({ endHour: h })}
                  onChangeMinute={(m) => updateQuietHours({ endMinute: m })}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Follow-up Reminder Days */}
      <section className={`p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 ${!localPrefs.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900 dark:text-gray-100">Follow-up Reminder Timing</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Days after applying to send a follow-up reminder</p>
          </div>
          <select
            value={localPrefs.followUpReminderDays}
            onChange={(e) => updatePrefs({ ...localPrefs, followUpReminderDays: parseInt(e.target.value) })}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={3}>3 days</option>
            <option value={5}>5 days</option>
            <option value={7}>7 days</option>
            <option value={10}>10 days</option>
            <option value={14}>14 days</option>
            <option value={21}>21 days</option>
            <option value={30}>30 days</option>
          </select>
        </div>
      </section>
    </div>
  )
}

// ============================================================================
// Event Type Card
// ============================================================================

interface EventTypeCardProps {
  eventType: NotificationEventType
  config: EventTypeConfig
  preference: NotificationTypePreference
  onToggle: () => void
  onToggleChannel: (channel: NotificationChannel) => void
  onSnoozeChange: (minutes: number) => void
}

function EventTypeCard({
  config,
  preference,
  onToggle,
  onToggleChannel,
  onSnoozeChange
}: EventTypeCardProps) {
  return (
    <div className="p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="space-y-3">
        {/* Header row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-gray-500 dark:text-gray-400">{config.icon}</div>
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">{config.label}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{config.description}</p>
            </div>
          </div>
          <ToggleSwitch checked={preference.enabled} onChange={onToggle} />
        </div>

        {/* Channel & snooze controls (visible when enabled) */}
        {preference.enabled && (
          <div className="flex items-center justify-between pl-7 pt-1">
            {/* Channel toggles */}
            <div className="flex items-center gap-4">
              <ChannelChip
                label="Browser"
                active={preference.channels.includes('browser')}
                onToggle={() => onToggleChannel('browser')}
              />
              <ChannelChip
                label="In-App"
                active={preference.channels.includes('in_app')}
                onToggle={() => onToggleChannel('in_app')}
              />
            </div>

            {/* Snooze duration */}
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 dark:text-gray-400">Snooze</label>
              <select
                value={preference.snoozeDurationMinutes}
                onChange={(e) => onSnoozeChange(parseInt(e.target.value))}
                className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-800 focus:ring-1 focus:ring-blue-500"
              >
                {SNOOZE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Channel Chip
// ============================================================================

interface ChannelChipProps {
  label: string
  active: boolean
  onToggle: () => void
}

function ChannelChip({ label, active, onToggle }: ChannelChipProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
        active
          ? 'bg-blue-50 dark:bg-blue-500/10 border-blue-300 dark:border-blue-500 text-blue-700 dark:text-blue-400'
          : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500'
      }`}
    >
      {label}
    </button>
  )
}

// ============================================================================
// Toggle Switch (reused from existing pattern)
// ============================================================================

interface ToggleSwitchProps {
  checked: boolean
  onChange: (value: boolean) => void
  disabled?: boolean
}

function ToggleSwitch({ checked, onChange, disabled }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-blue-600' : 'bg-gray-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  )
}

// ============================================================================
// Time Input (hour:minute)
// ============================================================================

interface TimeInputProps {
  hour: number
  minute: number
  onChangeHour: (h: number) => void
  onChangeMinute: (m: number) => void
}

function TimeInput({ hour, minute, onChangeHour, onChangeMinute }: TimeInputProps) {
  const pad = (n: number) => n.toString().padStart(2, '0')

  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        min={0}
        max={23}
        value={pad(hour)}
        onChange={(e) => {
          const v = parseInt(e.target.value)
          if (!isNaN(v) && v >= 0 && v <= 23) onChangeHour(v)
        }}
        className="w-12 px-2 py-1 text-sm border border-gray-300 rounded text-center focus:ring-1 focus:ring-blue-500"
      />
      <span className="text-gray-400">:</span>
      <input
        type="number"
        min={0}
        max={59}
        value={pad(minute)}
        onChange={(e) => {
          const v = parseInt(e.target.value)
          if (!isNaN(v) && v >= 0 && v <= 59) onChangeMinute(v)
        }}
        className="w-12 px-2 py-1 text-sm border border-gray-300 rounded text-center focus:ring-1 focus:ring-blue-500"
      />
    </div>
  )
}
